import React, { useEffect, useState } from "react";
import api from "../api";
import { useNavigate } from "react-router-dom";
import bgVideo from "../assets/lbg.mp4";
import { isStrongPassword, PASSWORD_POLICY_MESSAGE } from "../utils/passwordPolicy";

export default function AddTenant() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [contact, setContact] = useState("");
  const [address, setAddress] = useState("");
  const [barangay, setBarangay] = useState(null);
  const [validId, setValidId] = useState(null);
  const [contract, setContract] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(timer);
  }, [toast]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isStrongPassword(password)) {
      setToast({ message: PASSWORD_POLICY_MESSAGE, type: "error" });
      return;
    }

    try {
      setSubmitting(true);
      const form = new FormData();

      form.append("name", name);
      form.append("email", email);
      form.append("password", password);
      form.append("contact_number", contact);
      form.append("address", address);

      if (barangay) form.append("barangay_clearance", barangay);
      if (validId) form.append("valid_id", validId);
      if (contract) form.append("contract", contract);

      await api.post("/admin/tenants", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setToast({ message: "Tenant created successfully.", type: "success" });
      setTimeout(() => navigate("/admin/tenants"), 900);
    } catch (err) {
      console.error(err);
      const message =
        err.response?.data?.errors?.email?.[0] ||
        err.response?.data?.errors?.password?.[0] ||
        err.response?.data?.message ||
        "Create failed.";
      setToast({ message, type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full rounded-lg border border-white/10 bg-white px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30";

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 font-sans text-white">
      <div className="absolute inset-0">
        <video data-workspace-background="true" aria-hidden="true" className="h-full w-full object-cover" src={bgVideo} autoPlay muted loop playsInline />
        <div className="absolute inset-0 bg-slate-950/85" />
      </div>

      <div className="workspace-page-body relative z-10 min-h-screen px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto w-full max-w-3xl">
          <div className="mb-6 border-b border-white/10 pb-5">
            <p className="text-xs font-bold uppercase tracking-[0.35em] text-teal-300">
              Admin Tenants
            </p>
            <h1 className="mt-2 text-3xl font-bold tracking-wide md:text-4xl">
              Add Tenant
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-300 md:text-base">
              Create tenant account and upload required onboarding documents.
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="rounded-lg border border-white/10 bg-slate-950/90 p-5 shadow-2xl md:p-6"
          >
            <h2 className="mb-4">Tenant details</h2>
            <div className="ws-form-grid">
              <label className="ws-field">Full name<input type="text" autoComplete="name" value={name} onChange={(e)=>setName(e.target.value)} className={inputClass} required /></label>
              <label className="ws-field">Email address<input type="email" autoComplete="email" value={email} onChange={(e)=>setEmail(e.target.value)} className={inputClass} required /></label>
              <label className="ws-field">Password<input type="password" autoComplete="new-password" value={password} onChange={(e)=>setPassword(e.target.value)} className={inputClass} required /></label>
              <label className="ws-field">Contact number<input type="tel" autoComplete="tel" value={contact} onChange={(e)=>setContact(e.target.value)} className={inputClass}  /></label>
            </div>
            <p className="mt-3 text-xs text-slate-400">{PASSWORD_POLICY_MESSAGE}</p>
            <label className="ws-field mt-4">Address<textarea value={address} onChange={(e)=>setAddress(e.target.value)} autoComplete="street-address" className={inputClass} rows={3}/></label>
            <h2 className="mt-7">Onboarding documents</h2>
            <p className="mt-2 text-xs text-slate-400">Upload clear JPG or PNG copies for the tenant’s record.</p>

            <div className="mt-4 grid gap-3 md:grid-cols-3">
              {[
                ["Barangay Clearance", barangay, setBarangay],
                ["Valid ID", validId, setValidId],
                ["Contract", contract, setContract],
              ].map(([label, file, setter]) => (
                <label
                  key={label}
                  className="rounded-lg border border-white/10 bg-white/5 p-3"
                >
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                    {label}
                  </p>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={(e) => setter(e.target.files?.[0] || null)}
                    className="block w-full text-xs text-slate-300 file:mr-2 file:rounded-md file:border file:border-white/20 file:bg-slate-900 file:px-2 file:py-1 file:text-xs file:font-semibold file:text-slate-200 hover:file:bg-slate-800"
                  />
                  <p className="mt-2 truncate text-xs text-teal-200">
                    {file?.name || "No file selected"}
                  </p>
                </label>
              ))}
            </div>

            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => navigate("/admin/tenants")}
                className="rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:bg-white/10"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="rounded-lg bg-teal-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-teal-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? "Creating..." : "Create Tenant"}
              </button>
            </div>
          </form>
        </div>

        {toast && (
          <div
            className={`fixed right-4 top-4 z-50 rounded-lg px-5 py-3 text-sm font-semibold text-white shadow-2xl ${
              toast.type === "success" ? "bg-teal-600" : "bg-red-600"
            }`}
          >
            {toast.message}
          </div>
        )}
      </div>
    </div>
  );
}
